import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useIsDesktop, useResize, useScroll } from './index';
import { isSSR } from '../index';

type HomePageMenuState = {
  navRef: MutableRefObject<HTMLDivElement | null>;
  isNavFixed: boolean;
  isNavShadowed: boolean;
};

const scrollThrottleAmount = 20;
const resizeThrottleAmount = 200;

const getBannerOffset = (): number | undefined => {
  if (isSSR) {
    throw Error('getBannerOffset should never be used on the server');
  }
  // eslint-disable-next-line no-undef
  const bannerRef = document.getElementById('home-page-banner');
  const bannerY: number | undefined = bannerRef?.getBoundingClientRect().y;
  const bannerHeight: number | undefined = bannerRef?.getBoundingClientRect()
    .height;
  return typeof bannerY === 'number' && typeof bannerHeight === 'number'
    ? bannerY + bannerHeight
    : undefined;
};

export const useHomePageMenu = (): HomePageMenuState => {
  if (isSSR) {
    throw Error('useHomePageMenu should never be used on the server');
  }

  const isDesktop = useIsDesktop();
  const navRef = useRef<HTMLDivElement>(null);
  const { y } = useScroll({ wait: scrollThrottleAmount });

  const [isNavFixed, setIsNavFixed] = useState<boolean>(false);
  const [isNavShadowed, setIsNavShadowed] = useState<boolean>(false);

  // get the bannerOffset which is the bottom of the banner image
  const bannerOffset = getBannerOffset();

  // effect that runs every time the scroll y changes
  useEffect(() => {
    // return without doing anything if not on desktop
    if (!isDesktop) {
      return;
    }
    if (typeof bannerOffset === 'number') {
      const belowBannerBottom = bannerOffset <= 0;
      const belowShadowThreshold = bannerOffset + 80 <= 0;

      if (belowBannerBottom) {
        setIsNavFixed(true);
      } else {
        setIsNavFixed(false);
      }
      if (belowShadowThreshold) {
        setIsNavShadowed(true);
      } else {
        setIsNavShadowed(false);
      }
    }
  }, [y, bannerOffset]);

  const handleNavSetting = (): void => {
    const initialBannerOffset = getBannerOffset();
    const belowBannerBottom =
      typeof initialBannerOffset === 'number' ? initialBannerOffset <= 0 : 1;
    const belowShadowThreshold =
      typeof initialBannerOffset === 'number'
        ? initialBannerOffset + 80 <= 0
        : 1;

    // if below the banner than set the proper nav state
    if (belowBannerBottom) {
      setIsNavFixed(true);
    } else {
      setIsNavFixed(false);
    }
    if (belowShadowThreshold) {
      setIsNavShadowed(true);
    } else {
      setIsNavShadowed(false);
    }
  };

  // effect that runs on initial load
  useEffect(() => {
    handleNavSetting();
  }, []);

  useResize({
    callback: () => {
      handleNavSetting();
    },
    wait: resizeThrottleAmount,
  });

  return {
    navRef,
    isNavFixed,
    isNavShadowed,
  };
};

export default useHomePageMenu;
